import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveTemplate } from '../template';
import { resolveFieldName } from './populateDropdown';
const DEFAULT_SECTION_SELECTOR = '.sd-panel, .sd-row, fieldset';
export class ShowHideElementStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'showHideElement'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                fieldName: '',
                action: 'hide',
                scope: 'element',
                sectionSelector: '',
            }
        });
    }
    async execute(context) {
        const fieldName = resolveFieldName({
            fieldName: String(resolveTemplate(this.config.fieldName ?? '', context) ?? ''),
        });
        if (!fieldName) {
            context.log('[showHideElement] missing fieldName — skipped');
            return context;
        }
        const action = this.config.action || 'hide';
        const scope = this.config.scope || 'element';
        // Element scope: prefer SurveyJS visibility so the model stays in charge of layout.
        if (scope === 'element') {
            const model = context.form;
            const question = model?.getQuestionByName?.(fieldName);
            if (question && typeof question.visible === 'boolean') {
                const next = action === 'toggle' ? !question.visible : action === 'show';
                question.visible = next;
                context.log(`[showHideElement] ${fieldName} (survey question) visible=${next}`);
                return context;
            }
        }
        // DOM path: find by data-name, optionally climb to the section container.
        if (typeof document === 'undefined') {
            context.log('[showHideElement] no DOM available — skipped');
            return context;
        }
        const anchor = document.querySelector(`[data-name="${cssEscape(fieldName)}"]`);
        if (!anchor) {
            context.log(`[showHideElement] element not found for data-name="${fieldName}" — skipped`);
            return context;
        }
        let target = anchor;
        if (scope === 'section') {
            const selector = (typeof this.config.sectionSelector === 'string' &&
                this.config.sectionSelector.trim()) ||
                DEFAULT_SECTION_SELECTOR;
            const section = anchor.closest(selector);
            if (section) {
                target = section;
            }
            else {
                context.log(`[showHideElement] no ancestor matched "${selector}" — falling back to the element`);
            }
        }
        const next = applyDomVisibility(target, action);
        context.log(`[showHideElement] ${fieldName} (${scope}, DOM) visible=${next}`);
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        const action = cfg.action || 'hide';
        const scope = cfg.scope || 'element';
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Field name (SurveyJS name / data-name)</label>
        <input type="text" class="form-control" data-faf="fieldName" value="${escapeAttr(cfg.fieldName)}" placeholder="surveyjs_data_name" />
        <div class="text-muted small">Supports {{ }}.</div>
      </div>
      <div class="faf-id-row mb-3">
        <div class="faf-id-field">
          <label class="form-label">Action</label>
          <select class="form-control" data-faf="action">
            <option value="hide"${action === 'hide' ? ' selected' : ''}>hide</option>
            <option value="show"${action === 'show' ? ' selected' : ''}>show</option>
            <option value="toggle"${action === 'toggle' ? ' selected' : ''}>toggle</option>
          </select>
        </div>
        <div class="faf-id-field">
          <label class="form-label">Scope</label>
          <select class="form-control" data-faf="scope">
            <option value="element"${scope === 'element' ? ' selected' : ''}>element</option>
            <option value="section"${scope === 'section' ? ' selected' : ''}>section</option>
          </select>
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">Section selector (scope = section)</label>
        <input type="text" class="form-control" data-faf="sectionSelector" value="${escapeAttr(cfg.sectionSelector)}" placeholder="${escapeAttr(DEFAULT_SECTION_SELECTOR)}" />
        <div class="text-muted small">Ancestor of the field to show/hide. Defaults to the nearest panel/row.</div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.fieldName = inputValue(container, 'fieldName');
            this.config.action = selectValue(container, 'action') || 'hide';
            this.config.scope = selectValue(container, 'scope') || 'element';
            this.config.sectionSelector = inputValue(container, 'sectionSelector');
            onUpdated?.();
        };
        container.querySelectorAll('input, select').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
/** Apply show/hide/toggle to a DOM element via display; returns the resulting visibility. */
function applyDomVisibility(el, action) {
    const currentlyHidden = el.dataset.fafHidden === '1' || el.style.display === 'none';
    const shouldShow = action === 'toggle' ? currentlyHidden : action === 'show';
    if (shouldShow) {
        // Restore whatever display we stored (or clear to stylesheet default).
        el.style.display = el.dataset.fafPrevDisplay ?? '';
        delete el.dataset.fafPrevDisplay;
        delete el.dataset.fafHidden;
    }
    else {
        if (el.style.display && el.style.display !== 'none') {
            el.dataset.fafPrevDisplay = el.style.display;
        }
        el.style.display = 'none';
        el.dataset.fafHidden = '1';
    }
    return shouldShow;
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function selectValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function cssEscape(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
registerStep('showHideElement', ShowHideElementStep);
